import { useConfig } from "../containers/ConfigContext";
import { SentimentEnum, SentimentTypeEnum, sentimentMap, Sentiment } from "../models/sentiment.model";
import { Box, Button, Chip, CircularProgress, Divider, List, ListItem, ListItemAvatar, ListItemText, Stack } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { SentimentConfig, SentimentKeyword, SentimentKeywordRaw } from "../models/sentiment.model";
import { green, orange, red } from "@mui/material/colors";
import { titleStr } from "../lib/helper";
import { getRelatedSentiments } from "../actions/sentiment.action";
import { chunk } from "lodash";
import { TextAnalysisSentimentItem } from "./TextAnalysisSentimentsList";

const sentimentTypeColor = {
  [SentimentTypeEnum.all]: {
    color: "#222",
    bgcolor: "#eee",
  },
  [SentimentTypeEnum.positive]: {
    color: green[500],
    bgcolor: "#d2f8e5",
  },
  [SentimentTypeEnum.negative]: {
    color: red[500],
    bgcolor: "#ffd6d6",
  },
  [SentimentTypeEnum.neutral]: {
    color: orange[500],
    bgcolor: "#ffecc7",
  },
};

export default function TextAnalysisSentimentsDrawer(props: { keyword: SentimentKeyword; config?: SentimentConfig }) {
  const { keyword, config } = props;
  const { setLoading } = useConfig();
  const [sentimentType, setSentimentType] = React.useState<SentimentTypeEnum>(SentimentTypeEnum.all);
  const [hasMoreLoading, setHasMoreLoading] = useState(false);
  const [allComments, setAllComments] = useState<Sentiment[]>([]);
  const countMap = {
    [SentimentTypeEnum.all]: allComments.length,
    [SentimentEnum.positive]: allComments.filter((c) => c.type === SentimentEnum.positive).length,
    [SentimentEnum.negative]: allComments.filter((c) => c.type === SentimentEnum.negative).length,
    [SentimentEnum.neutral]: allComments.filter((c) => c.type === SentimentEnum.neutral).length,
  };

  const query_limit = 100;

  const filteredComments = useMemo(() => {
    return sentimentType === SentimentTypeEnum.all ? allComments : allComments.filter((c) => `${c.type}` === sentimentType);
  }, [allComments, sentimentType]);

  useEffect(() => {
    const loadComments = async () => {
      try {
        setLoading(true);
        console.log("loadComments", keyword);
        const { sentiments } = await getRelatedSentiments({
          sks: [...keyword.sentimentSks].slice(0, query_limit),
        });
        loadMore();
        setAllComments(sentiments);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    loadComments();
  }, [keyword]);

  const loadMore = async () => {
    if (keyword.sentimentSks.length > query_limit) {
      setHasMoreLoading(true);
      try {
        const rest = keyword.sentimentSks.slice(query_limit);
        for (const chunked of chunk(rest, 100)) {
          const { sentiments } = await getRelatedSentiments({
            sks: chunked,
          });
          setAllComments((prev) => [...prev, ...sentiments]);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setHasMoreLoading(false);
      }
    }
  };

  const onChanged = (sentiment: Sentiment) => {
    setAllComments((prev) => prev.map((s) => (s.questionKey === sentiment.questionKey && s.submitId === sentiment.submitId ? sentiment : s)));
  };

  const getKeywords = (keywords: SentimentKeywordRaw[]) => {
    let res = [];
    for (const item of keywords) {
      const existedConfig = config && config[item.text];
      if (
        existedConfig &&
        existedConfig.show !== false &&
        (existedConfig.label === keyword.label || existedConfig?.group === keyword.label)
      ) {
        res.push(item);
      } else if (keyword.label === item.text) {
        res.push(item);
      }
    }
    return res;
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Stack direction="row" spacing={2} alignItems="center" mb={2}>
        {[SentimentTypeEnum.all, SentimentTypeEnum.positive, SentimentTypeEnum.neutral, SentimentTypeEnum.negative].map((type) => (
          <Button
            variant={sentimentType === type ? "contained" : "text"}
            onClick={() => setSentimentType(type)}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              color: sentimentType === type ? "#fff" : sentimentTypeColor[type].color,
            }}
          >
            {titleStr(type)}
            <Box
              component="span"
              sx={{
                ml: 1,
                px: 1.2,
                py: 0.2,
                bgcolor: sentimentTypeColor[type].bgcolor,
                color: sentimentTypeColor[type].color,
                borderRadius: 2,
                fontSize: 14,
                fontWeight: 600,
                minWidth: 24,
                textAlign: "center",
                display: "inline-block",
              }}
            >
              {countMap[type]}
            </Box>
          </Button>
        ))}
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <List
          sx={{
            flex: 1,
            overflowY: "auto",
            mb: 0,
          }}
        >
          {filteredComments.map((c) => (
            <TextAnalysisSentimentItem
              key={c.id}
              sentiment={c}
              onChanged={onChanged}
              secondary={
                <Stack direction="row" spacing={1} flexWrap="wrap" mt={1}>
                  {getKeywords(c.keywords).map((a, i) => (
                    <Chip
                      key={`${c.id}-${a.text}-${i}`}
                      label={a.text}
                      size="small"
                      sx={{ bgcolor: sentimentTypeColor[a.sentiment].bgcolor }}
                    />
                  ))}
                </Stack>
              }
            />
          ))}
        </List>
        <div
          style={{
            height: 24,
            display: "flex",
            justifyContent: "center",
            marginTop: 8,
          }}
        >
          {hasMoreLoading && <CircularProgress size={24} />}
        </div>
      </Box>
    </Box>
  );
}